import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, Grid } from '@mui/material';

import {
  PageTitleAndSubTitle,
  TimeNavigatorChart,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';

import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import kpiTrackerMessages from '../../core-utils/messages/kpiTrackerMessages';

import Loader from '../../_components/molecules/Loader';
import { pageScrollDown } from '../SummaryPage';
import { COMMON_TEXTS } from '../../core-utils/messages/CommonConstants';
import { useStores } from '../../stores';
import { useLocation } from 'react-router-dom';
import {
  getHighEmphasisText,
  getMediumEmphasisText,
} from '../../core-utils/helper-utils/homePageHelper';
import axios from 'axios';
import properties from '../../core-utils/properties';
import {
  formatTimeMarkers,
  getFormattedChartData,
  getUrl,
} from '../../core-utils/helper-utils/kpiTrackerHelper';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { observer } from 'mobx-react';
import moment from 'moment';

export interface kpiInterface {
  name: string;
  selectedValues: string[];
  values: string[];
}
export interface locationProps {
  state: {
    selectedProgram: {
      program: string;
      timeMarkers: Array<any>;
      Kpis: kpiInterface[];
    };
  };
}

const KpiTrackerInternalPage: React.FC = observer(({}) => {
  const useStyles = makeStyles()({
    mainDiv: {
      backgroundColor: '',
      border: '',
      marginTop: '40px',
      marginLeft: '63px',
    },
    scrollBar: {
      '&::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
        backgroundColor: patientAccessTheme.palette.grey[300],
        borderRadius: '13px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: patientAccessTheme.palette.grey[300],
        borderRadius: '13px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: patientAccessTheme.palette.grey[500],
        borderRadius: '13px',
      },
    },
  });
  const { classes } = useStyles();

  const [loadingKpiTracker, setLoadingKpiTracker] = useState<boolean>(false);
  const location: locationProps = useLocation();
  const [selectedProgram] = useState(location?.state?.selectedProgram ?? {});
  const [modifiedData, setModifiedData] = useState<any>({
    program: '',
    Kpis: [],
  });
  const [timeMarkers, setTimeMarkers] = useState<any>([]);

  const handleDropdown = async (event: any, index: any) => {
    try {
      const value = event?.target?.value;
      const response = await getChartData(value);
      const data = await getFormattedChartData(response, value);
      setModifiedData((prev: any) => {
        const updatedKpis = { ...prev };
        updatedKpis.Kpis[index].dropDownSelected = value;
        updatedKpis.Kpis[index].chartData = data;
        updatedKpis.Kpis[index].subTitle = value;
        updatedKpis.Kpis[index].dataSchema = [
          {
            name: 'Time',
            type: 'date',
            format: value === 'Quality Measure Performance' ? '%Y' : '%-m/%Y',
          },
          {
            name: 'Value',
            type: 'number',
          },
        ];
        updatedKpis.Kpis[index].xAxisTitle =
          value === 'Quality Measure Performance' ? 'Year' : 'Months';
        updatedKpis.Kpis[index].outputTimeFormat =
          value === 'Quality Measure Performance' ? '%Y' : '%b-%Y';
        return updatedKpis;
      });
    } catch (error) {
      throw error;
    }
  };

  const getChartData = async (dropDownSelected: any) => {
    try {
      const url = await getUrl(dropDownSelected);
      const response = await axios.post(
        properties.aiqBaseUrl + properties.kpiTrackerBaseUrl + url,
        {
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          kpi_program: [selectedProgram.program],
        },
      );

      if (response?.status === 200) {
        if (dropDownSelected === 'Quality Measure Performance') {
          return response.data.data;
        }
        const result = [];
        const currentMonth = new Date(startDate);

        while (currentMonth <= new Date(endDate)) {
          const existingData = response.data.data.find((item: any) => {
            return (
              item.month === moment(startDate).month() + 1 &&
              item.service_year === moment(startDate).year()
            );
          });

          if (existingData) {
            result.push(existingData);
          } else {
            result.push({
              month: moment(startDate).month() + 1,
              service_year: moment(startDate).year(),
              value: 0,
            });
          }

          currentMonth.setMonth(currentMonth.getMonth() + 1);
        }
        return result;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  };

  const getData = async () => {
    let data: any;
    const formattedData = await Promise.allSettled(
      selectedProgram.Kpis.map(async (item: any) => {
        const dropDownSelected = item?.selectedValues[0];
        data = await getChartData(dropDownSelected);
        setLoadingKpiTracker(false);
        return {
          name: item?.name,
          selectedValues: item?.selectedValues,
          chartData: await getFormattedChartData(data, dropDownSelected),
          dropDownSelected: dropDownSelected,
          subTitle: `${dropDownSelected}`,
          dataSchema: [
            {
              name: 'Time',
              type: 'date',
              format:
                dropDownSelected === 'Quality Measure Performance'
                  ? '%Y'
                  : '%-m/%Y',
            },
            {
              name: 'Value',
              type: 'number',
            },
          ],
          isLoading: false,
          xAxisTitle:
            dropDownSelected === 'Quality Measure Performance'
              ? 'Year'
              : 'Months',
          outputTimeFormat:
            dropDownSelected === 'Quality Measure Performance' ? '%Y' : '%b-%Y',
        };
      }),
    );
    setModifiedData({
      program: selectedProgram.program,
      Kpis: formattedData.map((item: any) => item.value),
    });
  };

  const getTimeMarkers = async () => {
    setTimeMarkers(await formatTimeMarkers(selectedProgram.timeMarkers));
  };
  const apiCalls = async () => {
    setLoadingKpiTracker(true);
    await Promise.all([getData(), getTimeMarkers()]).then(async () => {
      setLoadingKpiTracker(false);
    });
  };

  const employerGroupStore = useStores().employerGroupStore;
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [areDatesReady, setAreDatesReady] = useState<boolean>(false);
  // useEffects
  useEffect(() => {
    Boolean(startDate !== '' && endDate !== '') ? apiCalls() : '';
  }, [areDatesReady]);

  useEffect(() => {
    setStartDate(
      `${employerGroupStore?.employerGroupResponseData?.start_month} ${employerGroupStore?.employerGroupResponseData?.start_year}`,
    );
    setEndDate(
      `${employerGroupStore?.employerGroupResponseData?.end_month} ${employerGroupStore?.employerGroupResponseData?.end_year}`,
    );
    setAreDatesReady((prev) => !prev);
  }, []);

  const getKpiList = () => {
    const kpis = selectedProgram?.Kpis?.map((item: any) => {
      return item?.name;
    }).filter(Boolean);
    if (kpis.length === 1) {
      return kpis[0];
    } else {
      const lastKPI = kpis[kpis.length - 1];
      const otherKPIs = kpis.slice(0, -1).join(', ');
      return `${otherKPIs} and ${lastKPI}`;
    }
  };

  const KpiTracker = () => {
    const [loader] = useState<any>(<Loader />);
    return (
      <div>
        {loadingKpiTracker ? (
          loader
        ) : (
          <>
            {modifiedData?.Kpis?.map((item: any, index: any) => (
              <>
                <div style={{ marginTop: '20px' }}>
                  <TimeNavigatorChart
                    chartData={item.chartData}
                    dataSchema={item.dataSchema}
                    timeMarker={timeMarkers}
                    yAxis={[
                      {
                        plot: {
                          value: 'Value',
                          type: 'line',
                          connectnulldata: true,
                        },
                        title: 'Cost($)',
                      },
                    ]}
                    subTitle={item.subTitle}
                    title={`${item.name} ${kpiTrackerMessages.pageTitles.title}`}
                    chartHeight={'350'}
                    height={'450px'}
                    paletteColors={[patientAccessTheme.palette.primary[400]]}
                    dashedStandardDropdown={{
                      width: '130px',
                      height: '22px',
                      options: item?.selectedValues,
                      displayValue: item?.selectedValues,
                      value: item.dropDownSelected,
                      handleChange: (event: any) => {
                        handleDropdown(event, index);
                      },
                    }}
                    isLoading={item.isLoading}
                    showLegend={false}
                    outputTimeFormat={item.outputTimeFormat}
                    customXAxisTitle={
                      <Grid
                        style={{
                          position: 'absolute',
                          bottom: '130px',
                          right: '23px',
                        }}
                      >
                        <Typography
                          fontSize="12px"
                          lineHeight="23px"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                        >
                          {item.xAxisTitle}
                        </Typography>
                      </Grid>
                    }
                  />
                </div>
              </>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className={classes.mainDiv}>
        <>
          <Box style={{ width: '1182px', position: 'relative' }}>
            <PageTitleAndSubTitle
              title={
                <>
                  {getHighEmphasisText(
                    kpiTrackerMessages.pageTitles.title + ' ',
                    true,
                  )}
                  {getMediumEmphasisText(COMMON_TEXTS.FOR + ' ', true)}
                  {getHighEmphasisText(selectedProgram.program, true)}
                </>
              }
              subTitle={
                kpiTrackerMessages.InternalPage.subTitle +
                getKpiList() +
                ' KPIs'
              }
              showDownloadButton={false}
              showQueryDataButton={false}
            />
          </Box>
          <KpiTracker />
        </>
        <Box id="ScrollElement"></Box>
      </div>
      {pageScrollDown('ScrollElement')}
    </div>
  );
});

export default KpiTrackerInternalPage;
